import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getPageBySlug, getPageComponents } from '../lib/pageService';
import { Loader } from 'lucide-react';
import FAQ from '../components/FAQ';
import PreferredBanks from '../components/PreferredBanks';
import Newsletter from '../components/Newsletter';
import BankComparison from '../components/BankComparison';
import CostTable from '../components/CostTable';
import BankInterestRates from '../components/BankInterestRates';
import { ArrowRight } from 'lucide-react';

const DynamicPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [pageData, setPageData] = useState<any>(null);
  const [pageComponents, setPageComponents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPageData = async () => {
      if (!slug) return;
      
      try {
        setIsLoading(true);
        setError(null);
        setNotFound(false);
        
        console.log(`Fetching page data for slug: ${slug}`);
        const page = await getPageBySlug(slug);
        
        if (page) {
          console.log(`Page found:`, page);
          setPageData(page);
          
          // Parse components from content if it's JSON
          if (page.template === 'json') {
            try {
              let content;
              if (typeof page.content === 'string') {
                content = JSON.parse(page.content);
                console.log('Parsed content from string:', content);
              } else {
                content = page.content;
                console.log('Content already parsed:', content);
              }
              
              if (content && content.components && Array.isArray(content.components)) {
                console.log('Found components in page content:', content.components);
                setPageComponents(content.components);
              } else {
                console.log('No components found in content, trying to fetch separately');
                // Try to fetch components separately
                const components = await getPageComponents(slug);
                console.log('Fetched components separately:', components);
                setPageComponents(components);
              }
            } catch (error) {
              console.error('Error parsing page content:', error);
              setError('Sayfa içeriği doğru formatta değil.');
              setPageComponents([]);
            }
          }
        } else {
          console.log(`Page not found for slug: ${slug}`);
          setNotFound(true);
        }
      } catch (err) {
        console.error(`Error fetching page with slug ${slug}:`, err);
        setError('Sayfa yüklenirken bir hata oluştu.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPageData();
  }, [slug]);

  // Render a component based on its type
  const renderComponent = (component: any) => {
    console.log('Rendering component:', component);
    
    if (!component || !component.type) {
      console.log('Invalid component or missing type:', component);
      return null;
    }
    
    switch (component.type) {
      case 'faq':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Sık Sorulan Sorular"}
                  </h2>
                  <p className="text-slate-600 mb-12">
                    {component.settings?.subtitle || "Merak ettiğiniz tüm sorular ve cevapları"}
                  </p>
                </div>
                <FAQ customFaqs={component.content?.faqs || []} />
              </div>
            </div>
          </div>
        );
      case 'bankList':
        return (
          <div key={component.id} className="py-16 bg-slate-50">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Birlikte Çalıştığımız Bankalar"}
                  </h2>
                  <p className="text-slate-600">
                    {component.settings?.subtitle || "Kredipark.com'da sadece avantajlı teklifler sunan bankaların ürünleri ve teklifleri yer almaktadır."}
                  </p>
                </div>
                <PreferredBanks customBanks={component.content?.bankLogos || []} />
              </div>
            </div>
          </div>
        );
      case 'newsletter':
        return <Newsletter key={component.id} />;
      case 'bankComparison':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Banka Karşılaştırma"}
                  </h2>
                  <p className="text-slate-600 mb-12">
                    {component.settings?.subtitle || "Bankaları karşılaştırın, size en uygun olanı seçin"}
                  </p>
                </div>
                <BankComparison />
              </div>
            </div>
          </div>
        );
      case 'costTable':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Maliyet Tablosu"}
                  </h2>
                  <p className="text-slate-600 mb-12">
                    {component.settings?.subtitle || "Kredi maliyetlerini şeffaf bir şekilde görüntüleyin"}
                  </p>
                </div>
                <CostTable />
              </div>
            </div>
          </div>
        );
      case 'interestRates':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Bankaların Faiz Oranları"}
                  </h2>
                  <p className="text-slate-600 mb-12">
                    {component.settings?.subtitle || "Güncel kredi faiz oranlarını karşılaştırın"}
                  </p>
                </div>
                <BankInterestRates />
              </div>
            </div>
          </div>
        );
      case 'textBlock':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                {component.settings?.showTitle && (
                  <h2 className="text-3xl font-bold mb-6 text-slate-800 text-center">
                    {component.settings?.title || "Metin Bloğu"}
                  </h2>
                )}
                <div 
                  className={`prose prose-slate max-w-none text-${component.settings?.textAlign || 'left'}`}
                  dangerouslySetInnerHTML={{ __html: component.content?.text || '<p>Metin içeriği</p>' }}
                />
              </div>
            </div>
          </div>
        );
      case 'imageBlock':
        return (
          <div key={component.id} className="py-16 bg-slate-50">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                {component.settings?.showTitle && (
                  <h2 className="text-3xl font-bold mb-6 text-slate-800 text-center">
                    {component.settings?.title || "Görsel Bloğu"}
                  </h2>
                )}
                <div className={`text-${component.settings?.imageAlign || 'center'}`}>
                  <img 
                    src={component.content?.imageUrl || 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&q=80&w=1200&h=600'} 
                    alt={component.content?.caption || 'Görsel'} 
                    className="rounded-lg max-w-full mx-auto"
                  />
                  {component.settings?.showCaption && component.content?.caption && (
                    <p className="text-sm text-slate-500 mt-2">{component.content.caption}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 'callToAction':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div 
                  className="text-center p-12 rounded-2xl shadow-lg"
                  style={{ backgroundColor: component.settings?.backgroundColor || '#ffffff' }}
                >
                  <h2 
                    className="text-3xl font-bold mb-4"
                    style={{ color: component.settings?.textColor || '#1e293b' }}
                  >
                    {component.content?.heading || 'Aksiyon Çağrısı Başlığı'}
                  </h2>
                  <p 
                    className="text-lg mb-8"
                    style={{ color: component.settings?.textColor || '#1e293b' }}
                  >
                    {component.content?.description || 'Kısa açıklama metni'}
                  </p>
                  <a 
                    href={component.content?.buttonUrl || '#'}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ backgroundColor: component.settings?.buttonColor || '#F7BA45', color: '#1e293b' }}
                  >
                    {component.settings?.buttonText || 'Hemen Başvur'}
                    <ArrowRight size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        console.log('Unknown component type:', component.type);
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader className="w-6 h-6 text-[#F7BA45] animate-spin" />
          <span className="text-slate-600">Sayfa yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
              <div className="text-center text-red-500">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render based on template type
  const renderPageContent = () => {
    if (!pageData) return null;

    switch (pageData.template) {
      case 'json':
        // For JSON template, render components
        return (
          <div className="min-h-screen bg-slate-50">
            {pageComponents.length > 0 ? (
              // Render components in order
              pageComponents
                .sort((a, b) => a.order - b.order)
                .map(component => renderComponent(component))
            ) : (
              // If no components, show error message
              <div className="container mx-auto px-4 py-16">
                <div className="max-w-4xl mx-auto">
                  <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6 text-center">
                    {pageData.title}
                  </h1>
                  <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
                    <p className="text-center text-slate-600 mb-4">
                      Bu sayfa için içerik bulunamadı veya içerik doğru formatta değil.
                    </p>
                    <pre className="whitespace-pre-wrap overflow-x-auto bg-slate-100 p-4 rounded-lg text-sm">
                      {typeof pageData.content === 'string' 
                        ? pageData.content 
                        : JSON.stringify(pageData.content, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'default':
        return (
          <div className="min-h-screen bg-slate-50">
            <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6 text-center">
                    {pageData.title}
                  </h1>
                  <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
                    <div dangerouslySetInnerHTML={{ __html: pageData.content }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'legal':
        return (
          <div className="min-h-screen bg-slate-50">
            <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6 text-center">
                    {pageData.title}
                  </h1>
                  <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
                    <div dangerouslySetInnerHTML={{ __html: pageData.content }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'article':
        return (
          <div className="min-h-screen bg-slate-50">
            <div className="container mx-auto px-4 py-16">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                  {pageData.title}
                </h1>
                <div className="flex items-center gap-4 text-slate-500 text-sm mb-8">
                  <span>Yazar: {pageData.author}</span>
                  <span>•</span>
                  <span>
                    {new Date(pageData.publishedAt || pageData.createdAt).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
                  <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: pageData.content }} />
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="min-h-screen bg-slate-50">
            <div className="container mx-auto px-4 py-16">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6 text-center">
                  {pageData.title}
                </h1>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
                  <div dangerouslySetInnerHTML={{ __html: pageData.content }} />
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return renderPageContent();
};

export default DynamicPage;